import { defaultBillsSortModel } from "@/services/bill/bill-grid-utils"
import { DateTime } from "luxon"

import type { GridSortModel } from "@mui/x-data-grid-pro"

export const SITE_BILLS_GRID_URL_KEYS = [
  "end",
  "meter",
  "resource_type",
  "sort",
  "start",
] as const

export interface SiteBillsGridUrlState {
  end: string | null
  meterId: string | null
  resource: string | null
  sortModel: GridSortModel
  start: string | null
}

export interface SiteBillsGridUrlWrite {
  end?: string | null
  meterId?: string | null
  resource?: string | null
  /** Set only when the person changed the column sort. */
  sortModel?: GridSortModel
  start?: string | null
}

const DEFAULT_BILLS_SORT_PARAM = "start_date:desc"

// The usage cell shows the as-ingested amount, but bill periods sort usage
// only by the standardized amount.
const gridFieldToSortField: Record<string, string> = {
  raw_usage: "usage_value",
}

const sortFieldToGridField: Record<string, string> = {
  usage_value: "raw_usage",
}

const sortableSortFields = new Set([
  "cost_major",
  "end_date",
  "resource_type",
  "start_date",
  "usage_value",
])

export const toSiteBillsSortParam = (sortModel: GridSortModel): string => {
  const firstSort = sortModel[0]
  if (!firstSort?.field || !firstSort.sort) {
    return DEFAULT_BILLS_SORT_PARAM
  }

  const sortField = gridFieldToSortField[firstSort.field] ?? firstSort.field
  return `${sortField}:${firstSort.sort}`
}

const readSortModel = (sortParam: string | null | undefined): GridSortModel => {
  const [sortField, direction] = sortParam?.split(":") ?? []
  if (
    !sortField ||
    !sortableSortFields.has(sortField) ||
    (direction !== "asc" && direction !== "desc")
  ) {
    return defaultBillsSortModel
  }

  return [
    { field: sortFieldToGridField[sortField] ?? sortField, sort: direction },
  ]
}

const readOptionalValue = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim() ?? ""
  return trimmed || null
}

const readIsoDate = (value: string | null | undefined): string | null => {
  const date = DateTime.fromISO(value?.trim() ?? "").startOf("day")
  return date.isValid ? date.toISODate() : null
}

export const readSiteBillsGridUrl = (
  values: Record<string, string | null>
): SiteBillsGridUrlState => {
  const start = readIsoDate(values.start)
  const end = readIsoDate(values.end)
  const inverted = start != null && end != null && start > end

  return {
    end: inverted ? null : end,
    meterId: readOptionalValue(values.meter),
    resource: readOptionalValue(values.resource_type),
    sortModel: readSortModel(values.sort),
    start: inverted ? null : start,
  }
}

export const writeSiteBillsGridUrl = (
  input: SiteBillsGridUrlWrite
): Record<string, string | null> => {
  const updates: Record<string, string | null> = {}

  if (input.end !== undefined) {
    updates.end = input.end
  }
  if (input.meterId !== undefined) {
    updates.meter = input.meterId
  }
  if (input.resource !== undefined) {
    updates.resource_type = input.resource
  }
  if (input.start !== undefined) {
    updates.start = input.start
  }
  if (input.sortModel) {
    updates.sort = toSiteBillsSortParam(input.sortModel)
  }

  return updates
}
